/**
 * ExecutiveHub.js - V1 Commercial Readiness Remediation
 * 
 * UNIFIED EXECUTIVE ENTRY POINT - COMPLETE
 * Consolidates:
 * - Executive Intelligence (AI scoring)
 * - Executive Dashboard V2 (Cockpit/KPIs)
 * - Executive Portfolio (Portfolio management)
 * - Executive Cockpit (Portfolio Heatmap, Blockers, Roadmap)
 * - Ask Analytics (Executive AI Questioning)
 * 
 * Solves: "Which dashboard should I use?" confusion
 * 
 * Design: Tab-based with persistent state, role-aware defaults
 * 
 * EXECUTIVE HUB COMPLETION SPRINT - December 2025
 * Restored capabilities:
 * - Phase 1: Ask Analytics Integration
 * - Phase 2: Portfolio Heatmap
 * - Phase 3: Strategic Roadmap Widget
 * - Phase 4: Top 5 Blockers
 * - Phase 5: Scope Health Widget
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Gauge, 
  Target, 
  Shield, 
  TrendingUp, 
  Briefcase,
  Brain,
  LayoutDashboard,
  AlertTriangle,
  CheckCircle,
  Activity,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  Clock,
  DollarSign,
  Users,
  Building2,
  FolderKanban,
  FileCheck,
  Zap,
  ArrowRight,
  Calendar,
  BarChart3,
  PieChart,
  HelpCircle,
  GitBranch,
  Map,
  Sparkles,
  FileX,
  GitCompare,
  MessageSquareText,
  Cpu,
  UserCheck,
  UserX,
  Thermometer,
  Wallet,
  TrendingDown,
  Receipt,
  Banknote,
  CircleDollarSign,
  Scale,
  Lightbulb,
  Download,
  FileSpreadsheet,
  FileText,
  CheckSquare,
  Square
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../context/AuthContext';
import { useActiveTenant } from '../hooks/useActiveTenant';
import { api } from '../api/client';
import { toast } from 'sonner';
import AskAnalyticsButton from '../components/AskAnalyticsButton';
import HierarchicalRoadmap from '../components/HierarchicalRoadmap';
import { useCurrency } from '../utils/currencyUtils';
import ExecutiveRecommendationsPanel from '../components/ExecutiveRecommendationsPanel';
import ScenarioIntelligencePanel from '../components/ScenarioIntelligencePanel';

// =============================================================================
// EXECUTIVE HUB SCORE CARD
// =============================================================================
const ScoreCard = ({ 
  title, 
  question, 
  score, 
  label, 
  color, 
  icon: Icon,
  onClick 
}) => {
  const getColorClasses = (color) => {
    const colors = {
      green: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-500',
      yellow: 'bg-amber-500/10 border-amber-500/30 text-amber-500',
      orange: 'bg-orange-500/10 border-orange-500/30 text-orange-500',
      red: 'bg-red-500/10 border-red-500/30 text-red-500',
      gray: 'bg-slate-500/10 border-slate-500/30 text-slate-500'
    };
    return colors[color] || colors.gray;
  };

  const getScoreColor = (score) => {
    if (score >= 90) return 'text-emerald-500';
    if (score >= 75) return 'text-amber-500';
    if (score >= 60) return 'text-orange-500';
    return 'text-red-500';
  };

  return (
    <Card 
      className={`cursor-pointer transition-all duration-300 hover:scale-[1.02] hover:shadow-lg border-2 ${getColorClasses(color)}`}
      onClick={onClick}
      data-testid={`score-card-${title.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Icon className="h-5 w-5" />
            <CardTitle className="text-base font-semibold">{title}</CardTitle>
          </div>
          <Badge variant="outline" className={getColorClasses(color)}>
            {label}
          </Badge>
        </div>
        <CardDescription className="text-sm mt-1">
          {question}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-end gap-2">
          <span className={`text-4xl font-bold ${getScoreColor(score)}`}>
            {score}%
          </span>
        </div>
      </CardContent>
    </Card>
  );
};

// =============================================================================
// EXECUTIVE KPI CARD
// =============================================================================
const KPICard = ({ title, value, subtitle, icon: Icon, color, trend, onClick }) => {
  const TrendIcon = trend === 'up' ? TrendingUp : trend === 'down' ? TrendingUp : null;
  
  return (
    <div 
      className={`bg-white rounded-lg border-l-4 p-4 shadow-sm cursor-pointer hover:shadow-md transition-all`}
      style={{ borderLeftColor: color }}
      onClick={onClick}
      data-testid={`kpi-${title.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="flex items-center justify-between mb-2">
        {Icon && <Icon className="w-5 h-5" style={{ color }} />}
        {TrendIcon && <TrendIcon className={`w-4 h-4 ${trend === 'up' ? 'text-green-500' : 'text-red-500 rotate-180'}`} />}
      </div>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      <p className="text-sm text-gray-600">{title}</p>
      {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
    </div>
  );
};

// =============================================================================
// INTERVENTION ALERT CARD
// =============================================================================
const InterventionAlert = ({ intervention, onClick }) => {
  const getSeverityStyles = (severity) => {
    if (severity === 'executive_intervention') return 'border-red-500 bg-red-50';
    if (severity === 'governance_review') return 'border-orange-500 bg-orange-50';
    return 'border-amber-500 bg-amber-50';
  };

  return (
    <div 
      className={`p-4 rounded-lg border-l-4 ${getSeverityStyles(intervention.severity)} cursor-pointer hover:shadow-md transition-all`}
      onClick={onClick}
    >
      <div className="flex items-start gap-3">
        <AlertTriangle className={`h-5 w-5 flex-shrink-0 ${
          intervention.severity === 'executive_intervention' ? 'text-red-500' : 'text-orange-500'
        }`} />
        <div className="flex-1">
          <p className="font-medium text-gray-900">{intervention.message}</p>
          <p className="text-sm text-gray-600 mt-1">
            Score: {intervention.score}%
          </p>
        </div>
        <ChevronRight className="h-5 w-5 text-gray-400" />
      </div>
    </div>
  );
};

// =============================================================================
// QUICK ACTION CARD
// =============================================================================
const QuickActionCard = ({ title, description, icon: Icon, color, onClick, count }) => {
  return (
    <div 
      className="p-4 bg-white rounded-lg border hover:shadow-md cursor-pointer transition-all group"
      onClick={onClick}
    >
      <div className="flex items-center gap-3">
        <div 
          className="w-10 h-10 rounded-lg flex items-center justify-center"
          style={{ backgroundColor: `${color}15` }}
        >
          <Icon className="w-5 h-5" style={{ color }} />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <p className="font-medium text-gray-900">{title}</p>
            {count !== undefined && count > 0 && (
              <Badge className="bg-red-500 text-white text-xs">{count}</Badge>
            )}
          </div>
          <p className="text-sm text-gray-500">{description}</p>
        </div>
        <ChevronRight className="w-5 h-5 text-gray-400 group-hover:translate-x-1 transition-transform" />
      </div>
    </div>
  );
};

// =============================================================================
// MAIN EXECUTIVE HUB COMPONENT
// =============================================================================
const ExecutiveHub = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const { tenantId } = useActiveTenant();
  const { formatCurrency, currencySymbol } = useCurrency();
  
  // Tab state from URL
  const [activeTab, setActiveTab] = useState(() => {
    const tab = searchParams.get('tab');
    return ['overview', 'intelligence', 'decisions', 'portfolio', 'actions', 'exec-packs'].includes(tab) ? tab : 'overview';
  });
  
  // Data state
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [intelligenceData, setIntelligenceData] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [error, setError] = useState(null);
  
  // Executive Packs state
  const [packLoading, setPackLoading] = useState(false);
  const [selectedPortfolios, setSelectedPortfolios] = useState([]);
  const [selectedPrograms, setSelectedPrograms] = useState([]);
  const [portfolioList, setPortfolioList] = useState([]);
  const [programList, setProgramList] = useState([]);
  
  // Executive Cockpit restoration data
  const [portfolioHeatmap, setPortfolioHeatmap] = useState([]);
  const [topBlockers, setTopBlockers] = useState([]);
  const [roadmapData, setRoadmapData] = useState([]);
  const [scopeHealthData, setScopeHealthData] = useState(null);
  const [roadmapScale, setRoadmapScale] = useState('quarter');
  const [showRoadmap, setShowRoadmap] = useState(false);
  
  // V1 COMPLETION: Resource, Financial, and Benefits Intelligence
  const [resourceIntelligence, setResourceIntelligence] = useState(null);
  const [financialIntelligence, setFinancialIntelligence] = useState(null);
  const [benefitsIntelligence, setBenefitsIntelligence] = useState(null);
  
  // Demo data fallbacks for executive widgets when no real data exists
  const DEMO_RESOURCE_DATA = {
    summary: {
      utilization_percent: 72,
      over_allocated_count: 3,
      under_utilized_count: 8
    },
    bottlenecks: [
      { skill: 'Cloud Architecture', severity: 'high' },
      { skill: 'DevOps Engineering', severity: 'medium' }
    ],
    bottleneck_count: 2,
    forecast: {
      next_quarter: 85,
      risk_level: 'medium'
    }
  };
  
  const DEMO_FINANCIAL_DATA = {
    total_budget: 2450000,
    total_actual: 1890000,
    variance_percent: 4.2,
    roi: 1.8,
    summary: {
      total_budget: 2450000,
      total_spent: 1890000,
      variance_percent: 4.2,
      roi: 1.8
    },
    forecast: {
      end_of_year: 2350000,
      variance_forecast: 3.1
    }
  };
  
  const DEMO_BENEFITS_DATA = {
    expected: 3200000,
    realized: 1450000,
    achieved_percent: 45.3,
    at_risk_count: 2,
    summary: {
      total_expected: 3200000,
      total_realized: 1450000,
      achieved_percent: 45.3,
      at_risk: 2
    }
  };
  
  const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

  // Sync tab to URL
  const handleTabChange = useCallback((newTab) => {
    setActiveTab(newTab);
    setSearchParams(prev => {
      const params = new URLSearchParams(prev);
      params.set('tab', newTab);
      return params;
    }, { replace: true });
  }, [setSearchParams]);

  // Fetch all executive data including restored cockpit data
  const fetchData = useCallback(async () => {
    if (!tenantId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('simpliview_token');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'X-Tenant-ID': tenantId
      };

      // Fetch intelligence data
      const intelligenceRes = await fetch(`${API_BASE}/api/intelligence/dashboard`, { headers });
      if (intelligenceRes.ok) {
        const data = await intelligenceRes.json();
        setIntelligenceData(data.dashboard);
      }

      // Fetch dashboard data (includes heatmap and blockers)
      try {
        const dashRes = await api.getExecutiveDashboard();
        if (dashRes.data) {
          setPortfolioHeatmap(dashRes.data.portfolio_heatmap || []);
          setTopBlockers(dashRes.data.top_blockers || []);
        }
      } catch (e) {
        console.warn('Executive dashboard data unavailable:', e);
      }

      // Fetch V2 dashboard data
      try {
        const dashResV2 = await api.getExecutiveDashboardV2({});
        setDashboardData(dashResV2.data);
      } catch (e) {
        console.warn('Dashboard V2 data unavailable:', e);
      }
      
      // Fetch roadmap data
      try {
        const roadmapRes = await api.getExecutiveRoadmapView({ scale: roadmapScale });
        setRoadmapData(roadmapRes.data?.items || []);
      } catch (e) {
        console.warn('Roadmap data unavailable:', e);
      }
      
      // Fetch scope health data
      try {
        const scopeRes = await api.getScopeHealth(tenantId);
        setScopeHealthData(scopeRes.data);
      } catch (e) {
        console.warn('Scope health data unavailable:', e);
      }
      
      // V1 COMPLETION: Fetch Resource Intelligence
      try {
        const resourceRes = await api.getResourceExecutiveDashboard();
        // Use real data if available, otherwise use demo data
        const data = resourceRes.data;
        if (data && (data.summary || data.utilization_percent !== undefined)) {
          setResourceIntelligence(data);
        } else {
          setResourceIntelligence(DEMO_RESOURCE_DATA);
        }
      } catch (e) {
        console.warn('Resource intelligence unavailable, using demo data:', e);
        // Fallback to summary if executive dashboard not available
        try {
          const summaryRes = await api.getResourceSummary();
          const data = summaryRes.data;
          if (data && (data.summary || data.utilization_percent !== undefined)) {
            setResourceIntelligence(data);
          } else {
            setResourceIntelligence(DEMO_RESOURCE_DATA);
          }
        } catch (e2) {
          console.warn('Resource summary unavailable, using demo data:', e2);
          setResourceIntelligence(DEMO_RESOURCE_DATA);
        }
      }
      
      // V1 COMPLETION: Fetch Financial Intelligence
      try {
        const financialRes = await api.getFinancialVariances();
        const data = financialRes.data;
        if (data && (data.total_budget !== undefined || data.summary?.total_budget !== undefined)) {
          setFinancialIntelligence(data);
        } else {
          setFinancialIntelligence(DEMO_FINANCIAL_DATA);
        }
      } catch (e) {
        console.warn('Financial intelligence unavailable, using demo data:', e);
        setFinancialIntelligence(DEMO_FINANCIAL_DATA);
      }
      
      // V1 COMPLETION: Fetch Benefits Intelligence
      try {
        const benefitsRes = await api.getBenefitsExecutiveDashboard();
        const data = benefitsRes.data;
        if (data && (data.expected !== undefined || data.summary?.total_expected !== undefined)) {
          setBenefitsIntelligence(data);
        } else {
          setBenefitsIntelligence(DEMO_BENEFITS_DATA);
        }
      } catch (e) {
        console.warn('Benefits intelligence unavailable, using demo data:', e);
        // Fallback to summary
        try {
          const summaryRes = await api.getBenefitsSummary();
          const data = summaryRes.data;
          if (data && (data.expected !== undefined || data.summary?.total_expected !== undefined)) {
            setBenefitsIntelligence(data);
          } else {
            setBenefitsIntelligence(DEMO_BENEFITS_DATA);
          }
        } catch (e2) {
          console.warn('Benefits summary unavailable, using demo data:', e2);
          setBenefitsIntelligence(DEMO_BENEFITS_DATA);
        }
      }

    } catch (err) {
      console.error('Failed to fetch executive data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [tenantId, API_BASE, roadmapScale]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
    toast.success('Data refreshed');
  };
  
  // Fetch portfolios and programs for Executive Packs
  const fetchPacksData = useCallback(async () => {
    if (!tenantId) return;
    try {
      // Use the API endpoints with proper params
      const [portfoliosRes, programsRes] = await Promise.all([
        api.get(`/api/portfolios?tenant_id=${tenantId}`).catch(() => ({ data: [] })),
        api.get(`/api/programs`).catch(() => ({ data: { programs: [] } }))
      ]);
      
      // Handle different response formats
      const portfolios = portfoliosRes.data?.portfolios || portfoliosRes.data || [];
      const programs = programsRes.data?.programs || programsRes.data || [];
      
      setPortfolioList(Array.isArray(portfolios) ? portfolios : []);
      setProgramList(Array.isArray(programs) ? programs : []);
    } catch (err) {
      console.warn('Failed to fetch packs data:', err);
    }
  }, [tenantId]);

  useEffect(() => {
    if (activeTab === 'exec-packs') {
      fetchPacksData();
    }
  }, [activeTab, fetchPacksData]);

  // Download Executive Pack (Excel)
  const downloadExecutivePack = async (type) => {
    setPackLoading(true);
    try {
      let portfolioIds = [];
      let programIds = [];
      
      if (type === 'all') {
        portfolioIds = portfolioList.map(p => p.id || p._id);
        programIds = programList.map(p => p.program_id || p.id || p._id);
      } else if (type === 'selected') {
        portfolioIds = selectedPortfolios;
        programIds = selectedPrograms;
      }
      
      const response = await api.post('/api/reports/executive-pack', {
        tenant_id: tenantId,
        portfolio_ids: portfolioIds,
        program_ids: programIds
      }, {
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Executive_Governance_Pack_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Executive Pack downloaded successfully');
    } catch (err) {
      console.error('Failed to download executive pack:', err);
      toast.error('Failed to generate executive pack');
    } finally {
      setPackLoading(false);
    }
  };

  // Download Executive Pack (PDF)
  const downloadExecutivePackPdf = async () => {
    setPackLoading(true);
    try {
      const response = await api.post('/api/reports/executive-pack-pdf', {
        tenant_id: tenantId
      }, {
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Executive_Governance_Pack_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Executive Pack PDF downloaded successfully');
    } catch (err) {
      console.error('Failed to download executive pack PDF:', err);
      toast.error('Failed to generate executive pack PDF');
    } finally {
      setPackLoading(false);
    }
  };

  const togglePortfolioSelection = (id) => {
    setSelectedPortfolios(prev => 
      prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]
    );
  };

  const toggleProgramSelection = (id) => {
    setSelectedPrograms(prev => 
      prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]
    );
  };

  const selectAllPortfolios = () => {
    setSelectedPortfolios(portfolioList.map(p => p.id || p._id));
  };

  const selectAllPrograms = () => {
    setProgramList(prev => prev);
    setSelectedPrograms(programList.map(p => p.program_id || p.id || p._id));
  };

  const clearSelection = () => {
    setSelectedPortfolios([]);
    setSelectedPrograms([]);
  };
  
  // Helper functions
  const getHealthColor = (health) => {
    if (health === 'Critical' || health === 'critical') return 'bg-red-500';
    if (health === 'At Risk' || health === 'at_risk') return 'bg-amber-500';
    return 'bg-emerald-500';
  };
  
  // Currency formatting moved to hook below

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-8">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse space-y-8">
            <div className="h-12 bg-gray-200 rounded w-1/3"></div>
            <div className="grid grid-cols-3 gap-6">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-48 bg-gray-200 rounded-xl"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const { scores, overall_health, interventions_required, program_rankings, portfolio_rankings } = intelligenceData || {};
  const kpis = dashboardData?.kpis || {};
  const actionRequired = dashboardData?.action_required || {};
  
  // Executive-specific suggested queries for Ask Analytics
  const executiveSuggestedQueries = [
    'Which portfolios are at risk?',
    'What requires intervention this week?',
    'Which benefits are underperforming?',
    'Why has Outcome Achievement declined?',
    'Which programs are most constrained?',
    'What is causing Intent Alignment degradation?',
    'Which governance actions are overdue?'
  ];

  return (
    <div className="min-h-screen bg-gray-50 p-6" data-testid="executive-hub">
      <div className="max-w-7xl mx-auto space-y-6">
        
        {/* Header with Ask Analytics */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <Gauge className="w-8 h-8 text-blue-600" />
              Executive Hub
            </h1>
            <p className="text-gray-500 mt-1">
              Your unified command center for portfolio health, governance, and decision intelligence
            </p>
          </div>
          <div className="flex items-center gap-3">
            {/* PHASE 1: Ask Analytics Integration */}
            <AskAnalyticsButton 
              context="executive_hub"
              filters={{ view: 'executive', tenant_id: tenantId }}
              entryPoint="executive_hub_header"
              suggestedQueries={executiveSuggestedQueries}
              data-testid="executive-ask-analytics"
            />
            <Button 
              variant="outline" 
              onClick={handleRefresh}
              disabled={refreshing}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button 
              variant="outline"
              onClick={() => navigate('/help/executive')}
            >
              <HelpCircle className="w-4 h-4 mr-2" />
              Help
            </Button>
          </div>
        </div>

        {/* Tab Navigation */}
        <Tabs value={activeTab} onValueChange={handleTabChange}>
          <TabsList className="grid w-full grid-cols-5 max-w-3xl bg-white border">
            <TabsTrigger value="overview" className="flex items-center gap-2" data-testid="tab-overview">
              <LayoutDashboard className="w-4 h-4" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="intelligence" className="flex items-center gap-2" data-testid="tab-intelligence">
              <Brain className="w-4 h-4" />
              Intelligence
            </TabsTrigger>
            <TabsTrigger value="decisions" className="flex items-center gap-2" data-testid="tab-decisions">
              <Lightbulb className="w-4 h-4" />
              Decisions
            </TabsTrigger>
            <TabsTrigger value="portfolio" className="flex items-center gap-2" data-testid="tab-portfolio">
              <Briefcase className="w-4 h-4" />
              Portfolio
            </TabsTrigger>
            <TabsTrigger value="actions" className="flex items-center gap-2" data-testid="tab-actions">
              <Zap className="w-4 h-4" />
              Actions
              {(interventions_required?.length > 0 || 
                (actionRequired.high_severity_risks?.length || 0) + 
                (actionRequired.cross_team_blockers?.length || 0) > 0) && (
                <Badge className="bg-red-500 text-white text-xs ml-1">
                  {(interventions_required?.length || 0) + 
                   (actionRequired.high_severity_risks?.length || 0) + 
                   (actionRequired.cross_team_blockers?.length || 0)}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="exec-packs" className="flex items-center gap-2" data-testid="tab-exec-packs">
              <FileCheck className="w-4 h-4" />
              Executive Packs
            </TabsTrigger>
          </TabsList>

          {/* ============ OVERVIEW TAB ============ */}
          <TabsContent value="overview" className="mt-6 space-y-6">
            
            {/* Organization Health Banner */}
            {overall_health && (
              <div className={`p-6 rounded-xl border-2 ${
                overall_health.classification === 'healthy' ? 'bg-emerald-50 border-emerald-200' :
                overall_health.classification === 'monitor' ? 'bg-amber-50 border-amber-200' :
                overall_health.classification === 'governance_review' ? 'bg-orange-50 border-orange-200' :
                'bg-red-50 border-red-200'
              }`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <Activity className={`w-12 h-12 ${
                      overall_health.classification === 'healthy' ? 'text-emerald-500' :
                      overall_health.classification === 'monitor' ? 'text-amber-500' :
                      overall_health.classification === 'governance_review' ? 'text-orange-500' :
                      'text-red-500'
                    }`} />
                    <div>
                      <p className="text-sm text-gray-600 uppercase tracking-wider">Organization Health</p>
                      <div className="flex items-center gap-3">
                        <span className={`text-4xl font-bold ${
                          overall_health.classification === 'healthy' ? 'text-emerald-600' :
                          overall_health.classification === 'monitor' ? 'text-amber-600' :
                          overall_health.classification === 'governance_review' ? 'text-orange-600' :
                          'text-red-600'
                        }`}>
                          {overall_health.score}%
                        </span>
                        <Badge className={
                          overall_health.classification === 'healthy' ? 'bg-emerald-100 text-emerald-700' :
                          overall_health.classification === 'monitor' ? 'bg-amber-100 text-amber-700' :
                          overall_health.classification === 'governance_review' ? 'bg-orange-100 text-orange-700' :
                          'bg-red-100 text-red-700'
                        }>
                          {overall_health.label}
                        </Badge>
                      </div>
                    </div>
                  </div>
                  <Button onClick={() => handleTabChange('intelligence')}>
                    View Details <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            )}

            {/* Three Core Scores */}
            {scores && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <ScoreCard
                  title="Intent Alignment"
                  question={scores.intent_alignment?.question || "Are we building what was approved?"}
                  score={scores.intent_alignment?.score || 0}
                  label={scores.intent_alignment?.label || 'N/A'}
                  color={scores.intent_alignment?.color || 'gray'}
                  icon={Target}
                  onClick={() => handleTabChange('intelligence')}
                />
                <ScoreCard
                  title="Decision Integrity"
                  question={scores.decision_integrity?.question || "Can we trust the governance process?"}
                  score={scores.decision_integrity?.score || 0}
                  label={scores.decision_integrity?.label || 'N/A'}
                  color={scores.decision_integrity?.color || 'gray'}
                  icon={Shield}
                  onClick={() => handleTabChange('intelligence')}
                />
                <ScoreCard
                  title="Outcome Achievement"
                  question={scores.outcome_achievement?.question || "Are we realizing the promised value?"}
                  score={scores.outcome_achievement?.score || 0}
                  label={scores.outcome_achievement?.label || 'N/A'}
                  color={scores.outcome_achievement?.color || 'gray'}
                  icon={TrendingUp}
                  onClick={() => handleTabChange('intelligence')}
                />
              </div>
            )}

            {/* Quick Access Actions */}
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <QuickActionCard
                  title="PM Daily Brief"
                  description="AI-generated recommendations"
                  icon={Brain}
                  color="#8b5cf6"
                  onClick={() => navigate('/ai/pm-assistant')}
                />
                <QuickActionCard
                  title="Decisions"
                  description="Track and approve decisions"
                  icon={FileCheck}
                  color="#3b82f6"
                  onClick={() => navigate('/decisions')}
                />
                <QuickActionCard
                  title="Portfolio Hierarchy"
                  description="Structure & relationships"
                  icon={Building2}
                  color="#10b981"
                  onClick={() => navigate('/portfolio/hierarchy')}
                />
                <QuickActionCard
                  title="Roadmap"
                  description="Strategic timeline"
                  icon={Calendar}
                  color="#f59e0b"
                  onClick={() => navigate('/roadmap')}
                />
              </div>
            </div>

            {/* KPI Strip */}
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Key Metrics</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                <KPICard
                  title="Delivery Confidence"
                  value={`${kpis.delivery_confidence || 0}%`}
                  icon={Shield}
                  color={kpis.delivery_confidence >= 70 ? '#10b981' : kpis.delivery_confidence >= 40 ? '#f59e0b' : '#ef4444'}
                />
                <KPICard
                  title="Portfolio Completion"
                  value={`${kpis.portfolio_completion || 0}%`}
                  icon={Target}
                  color="#3b82f6"
                />
                <KPICard
                  title="Critical Risks"
                  value={kpis.critical_risks || kpis.at_risk_count || 0}
                  icon={AlertTriangle}
                  color="#ef4444"
                  onClick={() => navigate('/issues?type=Risk')}
                />
                <KPICard
                  title="Blocked Items"
                  value={kpis.blocked_count || 0}
                  icon={AlertTriangle}
                  color="#ef4444"
                />
                <KPICard
                  title="Governance Rate"
                  value={`${kpis.governance_approval_rate || 0}%`}
                  icon={FileCheck}
                  color="#8b5cf6"
                />
                <KPICard
                  title="On-Time Delivery"
                  value={`${kpis.on_time_delivery_rate || 0}%`}
                  icon={CheckCircle}
                  color="#10b981"
                />
              </div>
            </div>

            {/* Interventions Required */}
            {interventions_required && interventions_required.length > 0 && (
              <Card className="border-red-200 bg-red-50">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-red-700">
                    <AlertTriangle className="w-5 h-5" />
                    Executive Actions Required ({interventions_required.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {interventions_required.slice(0, 3).map((intervention, idx) => (
                    <InterventionAlert 
                      key={idx} 
                      intervention={intervention}
                      onClick={() => handleTabChange('actions')}
                    />
                  ))}
                  {interventions_required.length > 3 && (
                    <Button variant="outline" className="w-full" onClick={() => handleTabChange('actions')}>
                      View All {interventions_required.length} Actions
                    </Button>
                  )}
                </CardContent>
              </Card>
            )}

            {/* ========================================================= */}
            {/* PHASE 2: PORTFOLIO HEATMAP - RESTORED FROM EXECUTIVE COCKPIT */}
            {/* ========================================================= */}
            <Card className="border-gray-200" data-testid="portfolio-heatmap-widget">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <PieChart className="w-5 h-5 text-blue-600" />
                    Portfolio Heatmap
                  </CardTitle>
                  <div className="flex items-center gap-4 text-sm">
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-3 rounded bg-red-500"></div>
                      <span className="text-gray-500">Critical</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-3 rounded bg-amber-500"></div>
                      <span className="text-gray-500">At Risk</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-3 rounded bg-emerald-500"></div>
                      <span className="text-gray-500">On Track</span>
                    </div>
                  </div>
                </div>
                <CardDescription>Where should I look first? Visual portfolio health at a glance.</CardDescription>
              </CardHeader>
              <CardContent>
                {portfolioHeatmap.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {portfolioHeatmap.map((portfolio) => (
                      <div
                        key={portfolio.id}
                        onClick={() => navigate(`/portfolio/${portfolio.id}`)}
                        className="border border-gray-200 rounded-lg p-4 hover:shadow-md cursor-pointer transition-all bg-white"
                        data-testid={`heatmap-portfolio-${portfolio.id}`}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <h3 className="font-semibold text-gray-900 truncate pr-2">{portfolio.name}</h3>
                          <div className={`w-4 h-4 rounded-full flex-shrink-0 ${getHealthColor(portfolio.health)}`}></div>
                        </div>
                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-500">% Complete</span>
                            <span className="font-medium text-gray-900">{portfolio.percent_complete || 0}%</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div 
                              className={`h-2 rounded-full ${
                                (portfolio.percent_complete || 0) >= 70 ? 'bg-emerald-500' :
                                (portfolio.percent_complete || 0) >= 40 ? 'bg-amber-500' : 'bg-red-500'
                              }`}
                              style={{ width: `${portfolio.percent_complete || 0}%` }}
                            ></div>
                          </div>
                          <div className="flex justify-between text-sm pt-2">
                            <div>
                              <span className="text-red-600 font-medium">{portfolio.blocked_count || 0}</span>
                              <span className="text-gray-400 ml-1">blocked</span>
                            </div>
                            <div>
                              <span className="text-amber-600 font-medium">{portfolio.behind_count || 0}</span>
                              <span className="text-gray-400 ml-1">behind</span>
                            </div>
                          </div>
                          {portfolio.variance !== undefined && (
                            <div className="text-right text-sm">
                              <span className={`font-medium ${portfolio.variance > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                                {portfolio.variance > 0 ? '+' : ''}{formatCurrency(portfolio.variance)}
                              </span>
                              <span className="text-gray-400 ml-1">variance</span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Building2 className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No portfolios configured</p>
                    <Button variant="outline" className="mt-4" onClick={() => navigate('/portfolio/hierarchy')}>
                      Create Portfolio
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* ========================================================= */}
            {/* PHASE 5: SCOPE HEALTH WIDGET - RESTORED FROM EXECUTIVE COCKPIT */}
            {/* ========================================================= */}
            <Card className="border-gray-200" data-testid="scope-health-widget">
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-2">
                  <GitCompare className="w-5 h-5 text-purple-600" />
                  Scope Health
                </CardTitle>
                <CardDescription>Are we still building what we approved? Scope drift and intent alignment.</CardDescription>
              </CardHeader>
              <CardContent>
                {scopeHealthData ? (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* No Baseline */}
                    <div 
                      className="bg-red-50 border border-red-200 rounded-lg p-4 cursor-pointer hover:shadow-md transition-all"
                      onClick={() => navigate('/scope-baselines')}
                      data-testid="scope-no-baseline"
                    >
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                          <FileX className="w-5 h-5 text-red-600" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-red-600">{scopeHealthData.no_baseline?.length || 0}</p>
                          <p className="text-sm text-red-600">No Baseline</p>
                        </div>
                      </div>
                      <p className="text-xs text-gray-500">Projects without approved scope baseline</p>
                    </div>
                    
                    {/* Pending Candidates */}
                    <div 
                      className="bg-amber-50 border border-amber-200 rounded-lg p-4 cursor-pointer hover:shadow-md transition-all"
                      onClick={() => navigate('/scope-baselines?view=pending')}
                      data-testid="scope-pending"
                    >
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                          <Clock className="w-5 h-5 text-amber-600" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-amber-600">{scopeHealthData.pending_candidates?.length || 0}</p>
                          <p className="text-sm text-amber-600">Pending Review</p>
                        </div>
                      </div>
                      <p className="text-xs text-gray-500">Scope changes awaiting governance approval</p>
                    </div>
                    
                    {/* Baselined */}
                    <div 
                      className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 cursor-pointer hover:shadow-md transition-all"
                      onClick={() => navigate('/scope-baselines')}
                      data-testid="scope-baselined"
                    >
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                          <CheckCircle className="w-5 h-5 text-emerald-600" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-emerald-600">
                            {scopeHealthData.baselined_projects || 0}/{scopeHealthData.total_projects || 0}
                          </p>
                          <p className="text-sm text-emerald-600">Baselined</p>
                        </div>
                      </div>
                      <p className="text-xs text-gray-500">Projects with approved scope baselines</p>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <GitCompare className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>Scope health data not available</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* ========================================================= */}
            {/* V1 COMPLETION: RESOURCE INTELLIGENCE WIDGET */}
            {/* Question: "Do we have enough capacity to deliver?" */}
            {/* ========================================================= */}
            <Card className="border-gray-200" data-testid="resource-intelligence-widget">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Users className="w-5 h-5 text-indigo-600" />
                      Resource Intelligence
                    </CardTitle>
                    <CardDescription>Do we have enough capacity to deliver?</CardDescription>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => navigate('/resources')}>
                    View All
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {resourceIntelligence ? (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {/* Utilization */}
                    <div 
                      className={`rounded-lg p-4 cursor-pointer hover:shadow-md transition-all ${
                        (resourceIntelligence.summary?.utilization_percent || resourceIntelligence.utilization_percent || 0) > 90 
                          ? 'bg-red-50 border border-red-200' 
                          : (resourceIntelligence.summary?.utilization_percent || resourceIntelligence.utilization_percent || 0) > 75 
                            ? 'bg-amber-50 border border-amber-200' 
                            : 'bg-emerald-50 border border-emerald-200'
                      }`}
                      onClick={() => navigate('/resources/utilisation')}
                      data-testid="resource-utilization"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <Thermometer className={`w-5 h-5 ${
                          (resourceIntelligence.summary?.utilization_percent || resourceIntelligence.utilization_percent || 0) > 90 
                            ? 'text-red-600' 
                            : (resourceIntelligence.summary?.utilization_percent || resourceIntelligence.utilization_percent || 0) > 75 
                              ? 'text-amber-600' 
                              : 'text-emerald-600'
                        }`} />
                        <span className="text-sm text-gray-600">Utilization</span>
                      </div>
                      <p className={`text-2xl font-bold ${
                        (resourceIntelligence.summary?.utilization_percent || resourceIntelligence.utilization_percent || 0) > 90 
                          ? 'text-red-600' 
                          : (resourceIntelligence.summary?.utilization_percent || resourceIntelligence.utilization_percent || 0) > 75 
                            ? 'text-amber-600' 
                            : 'text-emerald-600'
                      }`}>
                        {(resourceIntelligence.summary?.utilization_percent || resourceIntelligence.utilization_percent || 0).toFixed(0)}%
                      </p>
                    </div>
                    
                    {/* Over-allocated */}
                    <div 
                      className={`rounded-lg p-4 cursor-pointer hover:shadow-md transition-all ${
                        (resourceIntelligence.summary?.over_allocated_count || resourceIntelligence.over_allocated || 0) > 0 
                          ? 'bg-red-50 border border-red-200' 
                          : 'bg-emerald-50 border border-emerald-200'
                      }`}
                      onClick={() => navigate('/resources/capacity')}
                      data-testid="resource-overallocated"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <UserX className={`w-5 h-5 ${
                          (resourceIntelligence.summary?.over_allocated_count || resourceIntelligence.over_allocated || 0) > 0 
                            ? 'text-red-600' 
                            : 'text-emerald-600'
                        }`} />
                        <span className="text-sm text-gray-600">Overloaded</span>
                      </div>
                      <p className={`text-2xl font-bold ${
                        (resourceIntelligence.summary?.over_allocated_count || resourceIntelligence.over_allocated || 0) > 0 
                          ? 'text-red-600' 
                          : 'text-emerald-600'
                      }`}>
                        {resourceIntelligence.summary?.over_allocated_count || resourceIntelligence.over_allocated || 0}
                      </p>
                    </div>
                    
                    {/* Bottlenecks */}
                    <div 
                      className={`rounded-lg p-4 cursor-pointer hover:shadow-md transition-all ${
                        (resourceIntelligence.bottlenecks?.length || resourceIntelligence.bottleneck_count || 0) > 0 
                          ? 'bg-amber-50 border border-amber-200' 
                          : 'bg-emerald-50 border border-emerald-200'
                      }`}
                      onClick={() => navigate('/resources/capacity')}
                      data-testid="resource-bottlenecks"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <Cpu className={`w-5 h-5 ${
                          (resourceIntelligence.bottlenecks?.length || resourceIntelligence.bottleneck_count || 0) > 0 
                            ? 'text-amber-600' 
                            : 'text-emerald-600'
                        }`} />
                        <span className="text-sm text-gray-600">Bottlenecks</span>
                      </div>
                      <p className={`text-2xl font-bold ${
                        (resourceIntelligence.bottlenecks?.length || resourceIntelligence.bottleneck_count || 0) > 0 
                          ? 'text-amber-600' 
                          : 'text-emerald-600'
                      }`}>
                        {resourceIntelligence.bottlenecks?.length || resourceIntelligence.bottleneck_count || 0}
                      </p>
                    </div>
                    
                    {/* Available Capacity */}
                    <div 
                      className="bg-blue-50 border border-blue-200 rounded-lg p-4 cursor-pointer hover:shadow-md transition-all"
                      onClick={() => navigate('/resources')}
                      data-testid="resource-available"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <UserCheck className="w-5 h-5 text-blue-600" />
                        <span className="text-sm text-gray-600">Available</span>
                      </div>
                      <p className="text-2xl font-bold text-blue-600">
                        {resourceIntelligence.summary?.under_utilized_count || resourceIntelligence.available_capacity || 0}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>Resource data not available</p>
                    <Button variant="outline" className="mt-4" onClick={() => navigate('/resources')}>
                      Configure Resources
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* ========================================================= */}
            {/* V1 COMPLETION: FINANCIAL INTELLIGENCE WIDGET */}
            {/* Question: "Are we spending money effectively?" */}
            {/* ========================================================= */}
            <Card className="border-gray-200" data-testid="financial-intelligence-widget">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Wallet className="w-5 h-5 text-emerald-600" />
                      Financial Intelligence
                    </CardTitle>
                    <CardDescription>Are we spending money effectively?</CardDescription>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => navigate('/financial')}>
                    View All
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {financialIntelligence ? (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {/* Total Budget */}
                    <div 
                      className="bg-blue-50 border border-blue-200 rounded-lg p-4 cursor-pointer hover:shadow-md transition-all"
                      onClick={() => navigate('/financial')}
                      data-testid="financial-budget"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <Banknote className="w-5 h-5 text-blue-600" />
                        <span className="text-sm text-gray-600">Budget</span>
                      </div>
                      <p className="text-2xl font-bold text-blue-600">
                        {formatCurrency(financialIntelligence.total_budget || financialIntelligence.summary?.total_budget || 0)}
                      </p>
                    </div>
                    
                    {/* Actual Spend */}
                    <div 
                      className="bg-slate-50 border border-slate-200 rounded-lg p-4 cursor-pointer hover:shadow-md transition-all"
                      onClick={() => navigate('/financial?tab=variance')}
                      data-testid="financial-spent"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <Receipt className="w-5 h-5 text-slate-600" />
                        <span className="text-sm text-gray-600">Spent</span>
                      </div>
                      <p className="text-2xl font-bold text-slate-600">
                        {formatCurrency(financialIntelligence.total_actual || financialIntelligence.summary?.total_spent || 0)}
                      </p>
                    </div>
                    
                    {/* Variance */}
                    <div 
                      className={`rounded-lg p-4 cursor-pointer hover:shadow-md transition-all ${
                        (financialIntelligence.variance_percent || financialIntelligence.summary?.variance_percent || 0) > 10 
                          ? 'bg-red-50 border border-red-200' 
                          : (financialIntelligence.variance_percent || financialIntelligence.summary?.variance_percent || 0) > 5 
                            ? 'bg-amber-50 border border-amber-200' 
                            : 'bg-emerald-50 border border-emerald-200'
                      }`}
                      onClick={() => navigate('/financial?tab=variance')}
                      data-testid="financial-variance"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <TrendingDown className={`w-5 h-5 ${
                          (financialIntelligence.variance_percent || financialIntelligence.summary?.variance_percent || 0) > 10 
                            ? 'text-red-600' 
                            : (financialIntelligence.variance_percent || financialIntelligence.summary?.variance_percent || 0) > 5 
                              ? 'text-amber-600' 
                              : 'text-emerald-600'
                        }`} />
                        <span className="text-sm text-gray-600">Variance</span>
                      </div>
                      <p className={`text-2xl font-bold ${
                        (financialIntelligence.variance_percent || financialIntelligence.summary?.variance_percent || 0) > 10 
                          ? 'text-red-600' 
                          : (financialIntelligence.variance_percent || financialIntelligence.summary?.variance_percent || 0) > 5 
                            ? 'text-amber-600' 
                            : 'text-emerald-600'
                      }`}>
                        {(financialIntelligence.variance_percent || financialIntelligence.summary?.variance_percent || 0).toFixed(1)}%
                      </p>
                    </div>
                    
                    {/* ROI */}
                    <div 
                      className={`rounded-lg p-4 cursor-pointer hover:shadow-md transition-all ${
                        (financialIntelligence.roi || financialIntelligence.summary?.roi || 0) >= 1 
                          ? 'bg-emerald-50 border border-emerald-200' 
                          : 'bg-amber-50 border border-amber-200'
                      }`}
                      onClick={() => navigate('/governance/benefits')}
                      data-testid="financial-roi"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <CircleDollarSign className={`w-5 h-5 ${
                          (financialIntelligence.roi || financialIntelligence.summary?.roi || 0) >= 1 
                            ? 'text-emerald-600' 
                            : 'text-amber-600'
                        }`} />
                        <span className="text-sm text-gray-600">ROI</span>
                      </div>
                      <p className={`text-2xl font-bold ${
                        (financialIntelligence.roi || financialIntelligence.summary?.roi || 0) >= 1 
                          ? 'text-emerald-600' 
                          : 'text-amber-600'
                      }`}>
                        {(financialIntelligence.roi || financialIntelligence.summary?.roi || 0).toFixed(1)}x
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Wallet className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>Financial data not available</p>
                    <Button variant="outline" className="mt-4" onClick={() => navigate('/financial')}>
                      Configure Financials
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* ========================================================= */}
            {/* V1 COMPLETION: BENEFITS/OUTCOME INTELLIGENCE WIDGET */}
            {/* Question: "Are we getting the value we approved?" */}
            {/* ========================================================= */}
            <Card className="border-gray-200" data-testid="benefits-intelligence-widget">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Scale className="w-5 h-5 text-purple-600" />
                      Benefits Intelligence
                    </CardTitle>
                    <CardDescription>Are we getting the value we approved?</CardDescription>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => navigate('/governance/benefits')}>
                    View All
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {benefitsIntelligence ? (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {/* Expected Benefits */}
                    <div 
                      className="bg-blue-50 border border-blue-200 rounded-lg p-4 cursor-pointer hover:shadow-md transition-all"
                      onClick={() => navigate('/governance/benefits')}
                      data-testid="benefits-expected"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <Target className="w-5 h-5 text-blue-600" />
                        <span className="text-sm text-gray-600">Expected</span>
                      </div>
                      <p className="text-2xl font-bold text-blue-600">
                        {formatCurrency(benefitsIntelligence.total_expected || benefitsIntelligence.summary?.expected_value || 0)}
                      </p>
                    </div>
                    
                    {/* Realized Benefits */}
                    <div 
                      className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 cursor-pointer hover:shadow-md transition-all"
                      onClick={() => navigate('/governance/benefits')}
                      data-testid="benefits-realized"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <CheckCircle className="w-5 h-5 text-emerald-600" />
                        <span className="text-sm text-gray-600">Realized</span>
                      </div>
                      <p className="text-2xl font-bold text-emerald-600">
                        {formatCurrency(benefitsIntelligence.total_realized || benefitsIntelligence.summary?.realized_value || 0)}
                      </p>
                    </div>
                    
                    {/* Achievement Rate */}
                    <div 
                      className={`rounded-lg p-4 cursor-pointer hover:shadow-md transition-all ${
                        (benefitsIntelligence.achievement_rate || benefitsIntelligence.summary?.achievement_percent || 0) >= 80 
                          ? 'bg-emerald-50 border border-emerald-200' 
                          : (benefitsIntelligence.achievement_rate || benefitsIntelligence.summary?.achievement_percent || 0) >= 50 
                            ? 'bg-amber-50 border border-amber-200' 
                            : 'bg-red-50 border border-red-200'
                      }`}
                      onClick={() => navigate('/governance/benefits')}
                      data-testid="benefits-achievement"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <TrendingUp className={`w-5 h-5 ${
                          (benefitsIntelligence.achievement_rate || benefitsIntelligence.summary?.achievement_percent || 0) >= 80 
                            ? 'text-emerald-600' 
                            : (benefitsIntelligence.achievement_rate || benefitsIntelligence.summary?.achievement_percent || 0) >= 50 
                              ? 'text-amber-600' 
                              : 'text-red-600'
                        }`} />
                        <span className="text-sm text-gray-600">Achieved</span>
                      </div>
                      <p className={`text-2xl font-bold ${
                        (benefitsIntelligence.achievement_rate || benefitsIntelligence.summary?.achievement_percent || 0) >= 80 
                          ? 'text-emerald-600' 
                          : (benefitsIntelligence.achievement_rate || benefitsIntelligence.summary?.achievement_percent || 0) >= 50 
                            ? 'text-amber-600' 
                            : 'text-red-600'
                      }`}>
                        {(benefitsIntelligence.achievement_rate || benefitsIntelligence.summary?.achievement_percent || 0).toFixed(0)}%
                      </p>
                    </div>
                    
                    {/* At Risk */}
                    <div 
                      className={`rounded-lg p-4 cursor-pointer hover:shadow-md transition-all ${
                        (benefitsIntelligence.at_risk_count || benefitsIntelligence.summary?.at_risk || 0) > 0 
                          ? 'bg-red-50 border border-red-200' 
                          : 'bg-emerald-50 border border-emerald-200'
                      }`}
                      onClick={() => navigate('/governance/benefits?filter=at_risk')}
                      data-testid="benefits-at-risk"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <AlertTriangle className={`w-5 h-5 ${
                          (benefitsIntelligence.at_risk_count || benefitsIntelligence.summary?.at_risk || 0) > 0 
                            ? 'text-red-600' 
                            : 'text-emerald-600'
                        }`} />
                        <span className="text-sm text-gray-600">At Risk</span>
                      </div>
                      <p className={`text-2xl font-bold ${
                        (benefitsIntelligence.at_risk_count || benefitsIntelligence.summary?.at_risk || 0) > 0 
                          ? 'text-red-600' 
                          : 'text-emerald-600'
                      }`}>
                        {benefitsIntelligence.at_risk_count || benefitsIntelligence.summary?.at_risk || 0}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Scale className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>Benefits data not available</p>
                    <Button variant="outline" className="mt-4" onClick={() => navigate('/governance/benefits')}>
                      Configure Benefits
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* ========================================================= */}
            {/* PHASE 4: TOP 5 BLOCKERS - RESTORED FROM EXECUTIVE COCKPIT */}
            {/* ========================================================= */}
            <Card className="border-gray-200" data-testid="top-blockers-widget">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <GitBranch className="w-5 h-5 text-red-600" />
                      Top 5 Blocking Dependencies
                    </CardTitle>
                    <CardDescription>What is stopping delivery? Cross-program and cross-portfolio blockers.</CardDescription>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => navigate('/dependencies')}>
                    View All
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {topBlockers.length > 0 ? (
                  <div className="space-y-3">
                    {topBlockers.slice(0, 5).map((blocker, idx) => (
                      <div 
                        key={blocker.id || idx}
                        className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors"
                        onClick={() => navigate('/dependencies')}
                        data-testid={`top-blocker-${idx}`}
                      >
                        <span className="flex-shrink-0 w-8 h-8 rounded-full bg-red-100 flex items-center justify-center text-red-600 font-bold text-sm">
                          {idx + 1}
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 truncate">
                            {blocker.from_project} → {blocker.to_project}
                          </p>
                          <p className="text-sm text-gray-500 truncate">{blocker.description || 'No description'}</p>
                          <div className="flex items-center gap-2 mt-1">
                            {blocker.is_cross_program && (
                              <Badge className="bg-blue-100 text-blue-700 text-xs">Cross-Program</Badge>
                            )}
                            {blocker.is_cross_portfolio && (
                              <Badge className="bg-purple-100 text-purple-700 text-xs">Cross-Portfolio</Badge>
                            )}
                            {blocker.affected_benefits > 0 && (
                              <Badge className="bg-amber-100 text-amber-700 text-xs">{blocker.affected_benefits} Benefits Impacted</Badge>
                            )}
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="text-lg font-bold text-red-600">{blocker.aging || 0}</p>
                          <p className="text-xs text-gray-500">Days Aging</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <CheckCircle className="w-12 h-12 mx-auto mb-3 text-emerald-500 opacity-50" />
                    <p>No blocking dependencies</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* ========================================================= */}
            {/* PHASE 3: STRATEGIC ROADMAP - RESTORED FROM EXECUTIVE COCKPIT */}
            {/* ========================================================= */}
            <Card className="border-gray-200" data-testid="strategic-roadmap-widget">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Map className="w-5 h-5 text-blue-600" />
                      Strategic Roadmap
                    </CardTitle>
                    <CardDescription>Where are we going? Portfolio timeline and major initiatives.</CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <select
                      value={roadmapScale}
                      onChange={(e) => setRoadmapScale(e.target.value)}
                      className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-blue-500 bg-white"
                      data-testid="roadmap-scale-selector"
                    >
                      <option value="quarter">Quarter View</option>
                      <option value="month">Month View</option>
                    </select>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setShowRoadmap(!showRoadmap)}
                    >
                      {showRoadmap ? <ChevronUp className="w-4 h-4 mr-1" /> : <ChevronDown className="w-4 h-4 mr-1" />}
                      {showRoadmap ? 'Collapse' : 'Expand'}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => navigate('/roadmap')}>
                      Full View
                    </Button>
                  </div>
                </div>
              </CardHeader>
              {showRoadmap && (
                <CardContent>
                  {roadmapData.length > 0 ? (
                    <div className="h-[400px] overflow-auto border border-gray-200 rounded-lg">
                      <HierarchicalRoadmap 
                        data={roadmapData} 
                        scale={roadmapScale}
                        showReleaseBands={false}
                        maxDepth={2}
                        onItemClick={(item) => {
                          if (item.type === 'Portfolio') navigate(`/portfolio/${item.id}`);
                          else if (item.type === 'Program') navigate(`/program/${item.id}`);
                          else if (item.type === 'Project') navigate(`/project/${item.id}`);
                        }}
                      />
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>No roadmap data available</p>
                      <Button variant="outline" className="mt-4" onClick={() => navigate('/roadmap')}>
                        Configure Roadmap
                      </Button>
                    </div>
                  )}
                </CardContent>
              )}
              {!showRoadmap && (
                <CardContent>
                  <div className="text-center py-4 text-gray-500 bg-gray-50 rounded-lg">
                    <p className="text-sm">Click &ldquo;Expand&rdquo; to view the strategic roadmap</p>
                  </div>
                </CardContent>
              )}
            </Card>

          </TabsContent>

          {/* ============ INTELLIGENCE TAB ============ */}
          <TabsContent value="intelligence" className="mt-6 space-y-6">
            
            {/* Score Details */}
            {scores && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <ScoreCard
                  title="Intent Alignment"
                  question={scores.intent_alignment?.question || "Are we building what was approved?"}
                  score={scores.intent_alignment?.score || 0}
                  label={scores.intent_alignment?.label || 'N/A'}
                  color={scores.intent_alignment?.color || 'gray'}
                  icon={Target}
                />
                <ScoreCard
                  title="Decision Integrity"
                  question={scores.decision_integrity?.question || "Can we trust the governance process?"}
                  score={scores.decision_integrity?.score || 0}
                  label={scores.decision_integrity?.label || 'N/A'}
                  color={scores.decision_integrity?.color || 'gray'}
                  icon={Shield}
                />
                <ScoreCard
                  title="Outcome Achievement"
                  question={scores.outcome_achievement?.question || "Are we realizing the promised value?"}
                  score={scores.outcome_achievement?.score || 0}
                  label={scores.outcome_achievement?.label || 'N/A'}
                  color={scores.outcome_achievement?.color || 'gray'}
                  icon={TrendingUp}
                />
              </div>
            )}

            {/* Rankings Section */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Programs Requiring Attention */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FolderKanban className="w-5 h-5 text-gray-500" />
                    Programs Requiring Attention
                  </CardTitle>
                  <CardDescription>Lowest scoring programs by combined score</CardDescription>
                </CardHeader>
                <CardContent className="space-y-2">
                  {program_rankings && program_rankings.length > 0 ? (
                    program_rankings.map((program, idx) => (
                      <div 
                        key={program.program_id} 
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                        onClick={() => navigate(`/program/${program.program_id}`)}
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-gray-400 font-mono text-sm w-6">#{idx + 1}</span>
                          <span className="font-medium">{program.program_name || 'Unknown'}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`font-bold ${
                            program.combined_score >= 90 ? 'text-emerald-500' :
                            program.combined_score >= 75 ? 'text-amber-500' :
                            program.combined_score >= 60 ? 'text-orange-500' :
                            'text-red-500'
                          }`}>
                            {program.combined_score}%
                          </span>
                          <ChevronRight className="w-4 h-4 text-gray-400" />
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <CheckCircle className="w-8 h-8 mx-auto mb-2 text-emerald-500/50" />
                      <p>All programs healthy</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Portfolio Overview */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-gray-500" />
                    Portfolio Health Overview
                  </CardTitle>
                  <CardDescription>All portfolios ranked by combined score</CardDescription>
                </CardHeader>
                <CardContent className="space-y-2">
                  {portfolio_rankings && portfolio_rankings.length > 0 ? (
                    portfolio_rankings.map((portfolio, idx) => (
                      <div 
                        key={portfolio.portfolio_id} 
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                        onClick={() => navigate(`/portfolio/${portfolio.portfolio_id}`)}
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-gray-400 font-mono text-sm w-6">#{idx + 1}</span>
                          <span className="font-medium">{portfolio.portfolio_name || 'Unknown'}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`font-bold ${
                            portfolio.combined_score >= 90 ? 'text-emerald-500' :
                            portfolio.combined_score >= 75 ? 'text-amber-500' :
                            portfolio.combined_score >= 60 ? 'text-orange-500' :
                            'text-red-500'
                          }`}>
                            {portfolio.combined_score}%
                          </span>
                          <ChevronRight className="w-4 h-4 text-gray-400" />
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <Building2 className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p>No portfolios configured</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

          </TabsContent>

          {/* ============ DECISIONS TAB ============ */}
          <TabsContent value="decisions" className="mt-6 space-y-8">
            
            {/* Executive Recommendations Panel */}
            <ExecutiveRecommendationsPanel maxItems={5} />
            
            {/* Scenario Intelligence Panel */}
            <ScenarioIntelligencePanel />
            
          </TabsContent>

          {/* ============ PORTFOLIO TAB ============ */}
          <TabsContent value="portfolio" className="mt-6 space-y-6">
            
            {/* Quick Navigation to Portfolio Features */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <QuickActionCard
                title="Portfolio Hierarchy"
                description="View and manage structure"
                icon={Building2}
                color="#3b82f6"
                onClick={() => navigate('/portfolio/hierarchy')}
              />
              <QuickActionCard
                title="Investment Planning"
                description="Manage investments"
                icon={DollarSign}
                color="#10b981"
                onClick={() => navigate('/portfolio/investments')}
              />
              <QuickActionCard
                title="Scenario Planning"
                description="What-if analysis"
                icon={BarChart3}
                color="#8b5cf6"
                onClick={() => navigate('/scenarios')}
              />
              <QuickActionCard
                title="Benefits Dashboard"
                description="Track value realization"
                icon={TrendingUp}
                color="#f59e0b"
                onClick={() => navigate('/analytics/benefits')}
              />
              <QuickActionCard
                title="Roadmap"
                description="Strategic timeline"
                icon={Calendar}
                color="#6366f1"
                onClick={() => navigate('/roadmap')}
              />
              <QuickActionCard
                title="Capacity Planning"
                description="Resource allocation"
                icon={Users}
                color="#ec4899"
                onClick={() => navigate('/resources/capacity')}
              />
            </div>

            {/* Portfolio Rankings */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="w-5 h-5" />
                  Portfolio Health Summary
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {portfolio_rankings && portfolio_rankings.length > 0 ? (
                  portfolio_rankings.map((portfolio) => (
                    <div 
                      key={portfolio.portfolio_id} 
                      className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                      onClick={() => navigate(`/portfolio/${portfolio.portfolio_id}`)}
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-3 h-3 rounded-full ${
                          portfolio.combined_score >= 90 ? 'bg-emerald-500' :
                          portfolio.combined_score >= 75 ? 'bg-amber-500' :
                          portfolio.combined_score >= 60 ? 'bg-orange-500' :
                          'bg-red-500'
                        }`} />
                        <span className="font-medium">{portfolio.portfolio_name || 'Unknown'}</span>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="text-sm text-gray-500">Combined Score</p>
                          <p className={`font-bold ${
                            portfolio.combined_score >= 90 ? 'text-emerald-500' :
                            portfolio.combined_score >= 75 ? 'text-amber-500' :
                            portfolio.combined_score >= 60 ? 'text-orange-500' :
                            'text-red-500'
                          }`}>
                            {portfolio.combined_score}%
                          </p>
                        </div>
                        <ChevronRight className="w-5 h-5 text-gray-400" />
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Building2 className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No portfolios configured</p>
                    <Button variant="outline" className="mt-4" onClick={() => navigate('/portfolio/hierarchy')}>
                      Create Portfolio
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

          </TabsContent>

          {/* ============ ACTIONS TAB ============ */}
          <TabsContent value="actions" className="mt-6 space-y-6">
            
            {/* Interventions */}
            {interventions_required && interventions_required.length > 0 && (
              <Card className="border-red-200">
                <CardHeader className="bg-red-50">
                  <CardTitle className="flex items-center gap-2 text-red-700">
                    <AlertTriangle className="w-5 h-5" />
                    Executive Interventions Required ({interventions_required.length})
                  </CardTitle>
                  <CardDescription className="text-red-600">
                    Items requiring immediate executive attention based on intelligence scoring
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3 pt-4">
                  {interventions_required.map((intervention, idx) => (
                    <InterventionAlert 
                      key={idx} 
                      intervention={intervention}
                    />
                  ))}
                </CardContent>
              </Card>
            )}

            {/* High Severity Risks */}
            {actionRequired.high_severity_risks?.length > 0 && (
              <Card className="border-orange-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-orange-500" />
                    High Severity Risks ({actionRequired.high_severity_risks.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {actionRequired.high_severity_risks.slice(0, 5).map((risk) => (
                    <div 
                      key={risk.id}
                      className="flex items-center justify-between p-3 bg-orange-50 rounded-lg hover:bg-orange-100 cursor-pointer"
                      onClick={() => navigate(`/issues?id=${risk.id}`)}
                    >
                      <div className="flex-1">
                        <p className="font-medium">{risk.title || risk.description}</p>
                        <p className="text-sm text-gray-500">{risk.project_name}</p>
                      </div>
                      <Badge className="bg-orange-500 text-white">{risk.severity}</Badge>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Governance Decisions */}
            {actionRequired.governance_decisions?.length > 0 && (
              <Card className="border-blue-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileCheck className="w-5 h-5 text-blue-500" />
                    Pending Governance Decisions ({actionRequired.governance_decisions.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {actionRequired.governance_decisions.slice(0, 5).map((decision) => (
                    <div 
                      key={decision.id}
                      className="flex items-center justify-between p-3 bg-blue-50 rounded-lg hover:bg-blue-100 cursor-pointer"
                      onClick={() => navigate('/decisions')}
                    >
                      <div className="flex-1">
                        <p className="font-medium">{decision.title || decision.description}</p>
                        <p className="text-sm text-gray-500">{decision.project_name}</p>
                      </div>
                      <Badge className="bg-blue-500 text-white">Pending</Badge>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* No Actions State */}
            {(!interventions_required || interventions_required.length === 0) && 
             (!actionRequired.high_severity_risks || actionRequired.high_severity_risks.length === 0) &&
             (!actionRequired.governance_decisions || actionRequired.governance_decisions.length === 0) && (
              <Card>
                <CardContent className="py-12 text-center">
                  <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">All Clear!</h3>
                  <p className="text-gray-500">No executive actions required at this time.</p>
                </CardContent>
              </Card>
            )}

          </TabsContent>

          {/* ============ EXECUTIVE PACKS TAB ============ */}
          <TabsContent value="exec-packs" className="mt-6 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileSpreadsheet className="w-5 h-5 text-blue-600" />
                  Executive Governance Packs
                </CardTitle>
                <CardDescription>
                  Generate comprehensive executive summaries and portfolio health reports in Excel format.
                  Select specific portfolios and programmes or download all.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Quick Actions */}
                <div className="flex flex-wrap gap-4">
                  <Button 
                    onClick={() => downloadExecutivePack('all')}
                    disabled={packLoading}
                    className="bg-blue-600 hover:bg-blue-700"
                    data-testid="download-all-packs"
                  >
                    {packLoading ? (
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <FileSpreadsheet className="w-4 h-4 mr-2" />
                    )}
                    Excel: All Portfolios
                  </Button>
                  <Button 
                    onClick={() => downloadExecutivePack('selected')}
                    disabled={packLoading || (selectedPortfolios.length === 0 && selectedPrograms.length === 0)}
                    variant="outline"
                    data-testid="download-selected-packs"
                  >
                    {packLoading ? (
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <FileSpreadsheet className="w-4 h-4 mr-2" />
                    )}
                    Excel: Selected ({selectedPortfolios.length + selectedPrograms.length})
                  </Button>
                  <Button 
                    onClick={downloadExecutivePackPdf}
                    disabled={packLoading}
                    className="bg-red-600 hover:bg-red-700"
                    data-testid="download-pdf-pack"
                  >
                    {packLoading ? (
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <FileText className="w-4 h-4 mr-2" />
                    )}
                    PDF Report
                  </Button>
                  {(selectedPortfolios.length > 0 || selectedPrograms.length > 0) && (
                    <Button variant="ghost" onClick={clearSelection}>
                      Clear Selection
                    </Button>
                  )}
                </div>

                {/* Portfolios Selection */}
                <div className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold flex items-center gap-2">
                      <Briefcase className="w-4 h-4 text-blue-500" />
                      Portfolios ({portfolioList.length})
                    </h3>
                    <Button variant="ghost" size="sm" onClick={selectAllPortfolios}>
                      Select All
                    </Button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {portfolioList.map((portfolio) => {
                      const id = portfolio.id || portfolio._id;
                      const isSelected = selectedPortfolios.includes(id);
                      return (
                        <div
                          key={id}
                          onClick={() => togglePortfolioSelection(id)}
                          className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                            isSelected 
                              ? 'border-blue-500 bg-blue-50' 
                              : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            {isSelected ? (
                              <CheckSquare className="w-5 h-5 text-blue-600" />
                            ) : (
                              <Square className="w-5 h-5 text-gray-400" />
                            )}
                            <div className="flex-1">
                              <p className="font-medium text-sm">{portfolio.name}</p>
                              <p className="text-xs text-gray-500">
                                {portfolio.status || 'Active'}
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    {portfolioList.length === 0 && (
                      <p className="text-gray-500 text-sm col-span-3">No portfolios available</p>
                    )}
                  </div>
                </div>

                {/* Programmes Selection */}
                <div className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-indigo-500" />
                      Programmes ({programList.length})
                    </h3>
                    <Button variant="ghost" size="sm" onClick={selectAllPrograms}>
                      Select All
                    </Button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-[400px] overflow-y-auto">
                    {programList.map((program) => {
                      const id = program.program_id || program.id || program._id;
                      const isSelected = selectedPrograms.includes(id);
                      return (
                        <div
                          key={id}
                          onClick={() => toggleProgramSelection(id)}
                          className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                            isSelected 
                              ? 'border-indigo-500 bg-indigo-50' 
                              : 'border-gray-200 hover:border-indigo-300 hover:bg-gray-50'
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            {isSelected ? (
                              <CheckSquare className="w-5 h-5 text-indigo-600" />
                            ) : (
                              <Square className="w-5 h-5 text-gray-400" />
                            )}
                            <div className="flex-1">
                              <p className="font-medium text-sm">{program.name}</p>
                              <p className="text-xs text-gray-500">
                                {program.status || program.health || 'Active'}
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    {programList.length === 0 && (
                      <p className="text-gray-500 text-sm col-span-3">No programmes available</p>
                    )}
                  </div>
                </div>

                {/* Pack Contents Description */}
                <Card className="bg-slate-50 border-slate-200">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-slate-700">
                      What&apos;s Included in the Executive Pack
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-emerald-500" />
                          <span>Executive Summary with overall health scores</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-emerald-500" />
                          <span>Portfolio performance overview</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-emerald-500" />
                          <span>Programme status and health breakdown</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-emerald-500" />
                          <span>Project-level summary with RAG status</span>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-emerald-500" />
                          <span>Risk register with severity ratings</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-emerald-500" />
                          <span>Key milestones and upcoming deadlines</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-emerald-500" />
                          <span>Budget vs Actuals variance analysis</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-emerald-500" />
                          <span>Benefits realisation tracking</span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </CardContent>
            </Card>
          </TabsContent>

        </Tabs>

      </div>
    </div>
  );
};

export default ExecutiveHub;
